/**
 * The submission pack's own address: what is outstanding, what exists, what to make.
 *
 * @module router/programme-editor/submission/SubmissionIndex
 */

import React, { useEffect, useRef, useState } from "react";

import { PROGRAMME_SECTION_LABELS } from "@/components/governance/governance-stations";
import { NamesUnavailableAlert } from "@/components/shared/NamesUnavailableAlert";
import { userDisplayName } from "@/components/shared/user-display-name";
import { ArtefactList } from "@/components/submission/ArtefactList";
import { ExportDialog } from "@/components/submission/ExportDialog";
import { ReadinessChecklist } from "@/components/submission/ReadinessChecklist";
import {
  AsyncBoundary,
  Button,
  EmptyState,
  Heading,
  LinkButton,
  MutedText,
  Panel,
  useAsync,
  useToast,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import { DESCRIPTOR_TEMPLATES } from "@/export/descriptor-template-registry";
import { type DescriptorVariant, resolveDescriptorVariant } from "@/export/descriptor-variant";
import { resolveProgrammeLeadContact } from "@/export/programme-lead";
import { downloadSnapshotArtefact } from "@/export/snapshot-artefact";
import { exportProgrammeDescriptorWord } from "@/export/word";
import { useProgramme } from "@/hooks/useStore";
import {
  isReadyForFinalExport,
  useEditorReadiness,
  useEditorReadinessFailed,
} from "@/platform/editor-readiness-context";
import { useEditorBase } from "@/router/programme-editor/SurfaceLayout";
import { pathForStateKey, unlockedStateKeys } from "@/router/programme-editor/surfaces";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import type { ExportRecord, ReadinessBlocker, Snapshot } from "@/server/types";
import { lockedReason } from "@/state/step-locks";
import type { Programme } from "@/types";
import type { ProviderContact } from "@/types/provider";
import { withTenantProviderSettings } from "@/utils/provider-settings";

export function SubmissionIndex(): React.ReactElement {
  const {
    client,
    session,
    editorSync,
    platformUsers,
    platformDataError,
    refreshPlatformWorkspace,
    tenantProviderSettings,
  } = useWorkspaceOutletContext();
  const { programme } = useProgramme();
  const base = useEditorBase();
  const programmeId = editorSync.record?.id;
  // A change case's working copy has no readiness or artefacts of its own,
  // and the programme's are the approved revision's, not this change's.
  const changeCaseId = editorSync.record?.changeCaseId;
  const [exporting, setExporting] = useState(false);
  const generatePack = useRef<HTMLButtonElement>(null);
  const [beckoning, setBeckoning] = useState(false);
  useEffect(() => {
    if (!beckoning) {
      return undefined;
    }
    const settle = window.setTimeout(() => setBeckoning(false), 1600);
    return () => window.clearTimeout(settle);
  }, [beckoning]);
  const showGeneratePack = () => {
    const button = generatePack.current;
    if (!button) {
      return;
    }
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const { top, bottom } = button.getBoundingClientRect();
    const inView = top >= 0 && bottom <= window.innerHeight;
    // Optional-call: jsdom has no layout and omits scrollIntoView.
    button.scrollIntoView?.({ behavior: still ? "auto" : "smooth", block: "center" });
    button.focus({ preventScroll: true });
    // A long smooth scroll outlasts the ring, so it waits for the scroll to land.
    if (inView || still || !("onscrollend" in window)) {
      setBeckoning(true);
    } else {
      window.addEventListener("scrollend", () => setBeckoning(true), { once: true });
    }
  };
  const readiness = useEditorReadiness();
  const layoutReadFailed = useEditorReadinessFailed();

  const governance = useAsync(
    () => client.getProgrammeGovernance(programmeId!),
    [client, programmeId],
    { enabled: Boolean(programmeId) && !changeCaseId },
  );

  // The layout's read refreshes after each save; this page's own read stands
  // in until it answers, so the checklist and the pack never disagree.
  const shownReadiness =
    readiness ?? (governance.status === "ready" ? governance.data.readiness : null);
  // A change's working copy is never the final pack, and has no read to fail.
  const readinessComplete = shownReadiness ? isReadyForFinalExport(shownReadiness) : undefined;
  const packComplete = changeCaseId ? false : readinessComplete;
  // Unknown because a read failed, not because neither has answered yet.
  const readinessFailed = !shownReadiness && (layoutReadFailed || governance.status === "error");

  // Named and gated as the governance surface does, so the two never disagree
  // about which section a blocker lives in or whether it is open.
  const unlocked = unlockedStateKeys();
  const destinationFor = ({ target }: ReadinessBlocker) => {
    if (!target) {
      return {};
    }
    const section = PROGRAMME_SECTION_LABELS[target.section] ?? target.section;
    if (!unlocked.has(target.section)) {
      return { section, lockedReason: `${lockedReason(target.section)}.` };
    }
    const href = pathForStateKey(base, target.section, target.moduleId ?? undefined);
    return { section, href: href ?? undefined };
  };

  const nameFor = (userId: string): string =>
    userDisplayName(userId, {
      users: platformUsers,
      self: session.user,
      namesUnavailable: platformDataError !== null,
    });

  // The rebuild reads the store, so unsaved edits are not the revision the server names.
  const rebuildRevision =
    editorSync.record && !editorSync.unsaved ? String(editorSync.record.saveRevision) : undefined;

  const toast = useToast();
  // The current lead: a locked snapshot records no Programme Manager of its own.
  const programmeLead = (): Promise<ProviderContact | undefined> =>
    programmeId
      ? resolveProgrammeLeadContact(client, programmeId, platformUsers)
      : Promise.resolve(undefined);
  // Rebuilt on the template and version the preview recorded, from the unchanged draft.
  const regenerate = (row: ExportRecord): void => {
    const variant =
      (Object.keys(DESCRIPTOR_TEMPLATES) as DescriptorVariant[]).find(
        (candidate) => DESCRIPTOR_TEMPLATES[candidate].friendlyName === row.template,
      ) ?? resolveDescriptorVariant(programme);
    programmeLead()
      .then((lead) =>
        exportProgrammeDescriptorWord(
          withTenantProviderSettings(programme, tenantProviderSettings),
          variant,
          row.versionId,
          lead,
        ),
      )
      .catch(() => {
        toast({ message: "The file could not be built. Try again.", tone: "warning" });
      });
  };
  const download = (row: ExportRecord, snapshot: Snapshot): void => {
    (row.format === "descriptor" ? programmeLead() : Promise.resolve(undefined))
      .then((lead) =>
        downloadSnapshotArtefact(
          row.format,
          withTenantProviderSettings(snapshot.data as unknown as Programme, tenantProviderSettings),
          lead,
        ),
      )
      .catch(() => {
        toast({ message: "The file could not be built. Try again.", tone: "warning" });
      });
  };

  return (
    <div className="space-y-4" data-testid="submission-index">
      {platformDataError ? (
        <NamesUnavailableAlert
          onRetry={refreshPlatformWorkspace}
          data-testid="submission-names-error"
        />
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Heading level={2} className="sr-only">
          Submission pack
        </Heading>
        <MutedText>
          Check what is left to complete, view your exports and download the submission pack.
        </MutedText>
        <Button
          variant="primary"
          icon="export"
          onClick={() => setExporting(true)}
          ref={generatePack}
          id="submission-generate-pack"
          // A ring that fades as it settles, so the eye lands on the button.
          className={`transition-shadow duration-700 ${beckoning ? "ring-4 ring-primary/40 ring-offset-2 ring-offset-base-100" : ""}`}
          data-testid="submission-generate-pack"
        >
          Generate pack
        </Button>
      </div>
      {exporting ? (
        <ExportDialog
          open
          onClose={() => setExporting(false)}
          programme={programme}
          isComplete={packComplete}
          readinessFailed={readinessFailed}
          onPreviewRecorded={governance.reload}
        />
      ) : null}
      {changeCaseId ? (
        <EmptyState
          icon="git-branch"
          title="This change has no readiness of its own"
          description="Its review and approval are tracked on Governance."
          action={<LinkButton to={`${base}/governance`}>Open Governance</LinkButton>}
          data-testid="submission-change-case-note"
        />
      ) : (
        <AsyncBoundary
          state={governance}
          onRetry={governance.reload}
          loading={
            <div className="space-y-4">
              <Panel title="Export gates" icon="checks">
                <ListBones rows={4} />
              </Panel>
              <Panel title="Artefacts" icon="files">
                <ListBones rows={2} />
              </Panel>
            </div>
          }
          empty={
            <EmptyState
              icon="seal-check"
              title="No governance record yet"
              description="Readiness appears once the programme has been opened."
            />
          }
        >
          {(summary) => (
            <div className="space-y-4">
              <ReadinessChecklist
                readiness={readiness ?? summary.readiness}
                destinationFor={destinationFor}
              />
              <ArtefactList
                exports={summary.exports}
                snapshots={summary.snapshots}
                nameFor={nameFor}
                onDownload={download}
                currentRevision={rebuildRevision}
                onRegenerate={regenerate}
                onShowGeneratePack={showGeneratePack}
              />
            </div>
          )}
        </AsyncBoundary>
      )}
    </div>
  );
}

export default SubmissionIndex;
